// garuda - Spectacles (Reviews) Page

import React, { useState, useEffect } from "react";
import { useNavigation } from "../context/NavigationContext";
import { TildeBg } from "../components/Icons";
import MarkdownRenderer from "../components/MarkdownRenderer";
import { formatTimestamp } from "../utils/helpers";
import JackpotWord from "../components/JackpotWord";
import { fetchAllContent } from "../services/contentService";
import PageNavigation from "../components/PageNavigation";
import "./Spectacles.css";

const Spectacles = () => {
  const { navigateTo } = useNavigation();
  const [spectacles, setSpectacles] = useState([]);
  const [filteredSpectacles, setFilteredSpectacles] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSpectacle, setSelectedSpectacle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("All");
  const [isReadingMode, setIsReadingMode] = useState(true);

  // Load content from files on mount
  useEffect(() => {
    const loadSpectacles = async () => {
      setLoading(true);
      const content = await fetchAllContent("spectacles");
      setSpectacles(content);
      setFilteredSpectacles(content);
      setLoading(false);
    };
    loadSpectacles();
  }, []);

  // Search and filter functionality
  useEffect(() => {
    let filtered = spectacles;

    // Filter by type
    if (filterType !== "All") {
      filtered = filtered.filter((s) => s.type === filterType);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (spectacle) =>
          spectacle.title.toLowerCase().includes(query) ||
          (spectacle.tags &&
            spectacle.tags.some((tag) => tag.toLowerCase().includes(query))),
      );
    }

    setFilteredSpectacles(filtered);
  }, [searchQuery, filterType, spectacles]);

  const handleSpectacleClick = (spectacle) => {
    setSelectedSpectacle(spectacle);
  };

  const handleBack = () => {
    setSelectedSpectacle(null);
  };

  const toggleReadingMode = () => {
    setIsReadingMode(!isReadingMode);
  };

  // Apply reading mode class to body
  useEffect(() => {
    if (isReadingMode) {
      document.body.classList.add("reading-mode");
    } else {
      document.body.classList.remove("reading-mode");
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove("reading-mode");
    };
  }, [isReadingMode]);

  // Get unique types for filter
  const types = [
    "All",
    ...new Set(spectacles.map((s) => s.type).filter(Boolean)),
  ];

  // Get resonance color
  const getResonanceColor = (resonance) => {
    const value = parseInt(resonance) || 0;
    if (value >= 80) return "#888";
    if (value >= 60) return "#777";
    if (value >= 40) return "#666";
    return "#555";
  };

  // Detail/Reading View
  if (selectedSpectacle) {
    return (
      <div className="spectacles-container detail-view">
        <PageNavigation
          isReadingMode={isReadingMode}
          onToggleReadingMode={toggleReadingMode}
          showReadingMode={true}
        />
        <TildeBg opacity={0.03} />

        <div className="spectacle-detail-header">
          <button className="back-button" onClick={handleBack}>
            ← Back to Spectacles
          </button>
        </div>

        <article className="spectacle-content">
          <h1 className="spectacle-title">{selectedSpectacle.title}</h1>
          <div className="author-section">
            <img
              src="/images/profile.jpg"
              alt="Prem Agarwal"
              className="author-avatar"
              onError={(e) => {
                e.target.style.display = "none";
                e.target.nextSibling.style.display = "flex";
              }}
            />
            <div
              className="author-avatar author-avatar-fallback"
              style={{ display: "none" }}
            >
              PA
            </div>
            <div className="author-info">
              <div className="author-name">Prem Agarwal</div>
              <div className="author-timestamp">
                {formatTimestamp(selectedSpectacle.timestamp)}
              </div>
            </div>
          </div>
          <div className="spectacle-meta">
            <span className="spectacle-type">{selectedSpectacle.type}</span>
          </div>

          {selectedSpectacle.resonance && (
            <div className="resonance-display">
              <div className="resonance-label-display">
                Resonance: {selectedSpectacle.resonance}%
              </div>
              <div className="resonance-bar-container">
                <div
                  className="resonance-bar"
                  style={{
                    width: `${selectedSpectacle.resonance}%`,
                    backgroundColor: getResonanceColor(
                      selectedSpectacle.resonance,
                    ),
                  }}
                />
              </div>
            </div>
          )}

          {selectedSpectacle.tags && selectedSpectacle.tags.length > 0 && (
            <div className="spectacle-tags-detail">
              {selectedSpectacle.tags.map((tag, idx) => (
                <span key={idx} className="tag">
                  #{tag}
                </span>
              ))}
            </div>
          )}

          <div className="spectacle-review">
            <MarkdownRenderer content={selectedSpectacle.content} />
          </div>
        </article>
      </div>
    );
  }

  // List View
  return (
    <div className="spectacles-container">
      <PageNavigation />
      <TildeBg opacity={0.05} />

      <div className="spectacles-header">
        <h1 className="page-title">
          <JackpotWord onClick={() => navigateTo("home")}>
            Spectacles
          </JackpotWord>
        </h1>
        <p className="page-subtitle">Through the lens of perception.</p>
      </div>

      {/* Search Bar */}
      <div className="search-container">
        <input
          type="text"
          className="search-input"
          placeholder="search spectacles_"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Filter Bar */}
      <div className="filter-bar">
        {types.map((type) => (
          <button
            key={type}
            className={`filter-button ${filterType === type ? "active" : ""}`}
            onClick={() => setFilterType(type)}
          >
            [{type}]
          </button>
        ))}
      </div>

      <div className="spectacles-list">
        {loading ? (
          <p className="no-results">Loading spectacles...</p>
        ) : filteredSpectacles.length === 0 ? (
          <p className="no-results">No spectacles found.</p>
        ) : (
          filteredSpectacles.map((spectacle) => (
            <div key={spectacle.id} className="spectacle-item">
              <div className="spectacle-item-header">
                <div>
                  <h2 className="spectacle-item-title">
                    <JackpotWord
                      onClick={() => handleSpectacleClick(spectacle)}
                    >
                      {spectacle.title}
                    </JackpotWord>
                  </h2>
                  <span className="spectacle-item-type">{spectacle.type}</span>
                </div>
              </div>

              {spectacle.resonance && (
                <div className="resonance-display-small">
                  <div className="resonance-bar-container-small">
                    <div
                      className="resonance-bar-small"
                      style={{
                        width: `${spectacle.resonance}%`,
                        backgroundColor: getResonanceColor(spectacle.resonance),
                      }}
                    />
                  </div>
                  <span className="resonance-value-small">
                    {spectacle.resonance}%
                  </span>
                </div>
              )}

              <p className="spectacle-timestamp-small">
                {formatTimestamp(spectacle.timestamp)}
              </p>

              {spectacle.tags && spectacle.tags.length > 0 && (
                <div className="spectacle-tags">
                  {spectacle.tags.map((tag, idx) => (
                    <span key={idx} className="tag">
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Spectacles;
